import { useMemo, useState } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { Icon } from '@components/icons';
import PasswordSection from '@sections/settings/security/password';
import MfaSection from '@sections/settings/security/mfa';
import { useUserContext } from '@hooks/context/useUserContext';
import { prettifyDate } from '@utils/date';
import { ETwoFAMethod } from '@xeppt/xeppt-sdk/types';

const SettingsSecurityLayout = () => {
    const { handleBack } = useNavigationContext();
    const [activeTab, setActiveTab] = useState<'password' | 'mfa'>();
    const { user } = useUserContext();
    const { submitLocale } = useLocales();

    const renderTitle = useMemo(() => {
        if (activeTab === 'password') {
            return 'Change Password';
        }
        if (activeTab === 'mfa') {
            return 'Two-Factor Authentication';
        }
        return 'Security';
    }, [activeTab]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() =>
                        !!activeTab ? setActiveTab(undefined) : handleBack(routes.settings)
                    }>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">{renderTitle}</Typography>
            </div>
            {!activeTab && (
                <div className={styles.methods_wrapper}>
                    <div className={styles.method} onClick={() => setActiveTab('password')}>
                        <div className={styles.left}>
                            <div className={styles.icon}>
                                <Icon name="lock" />
                            </div>
                            <div className={styles.text_wrapper}>
                                <Typography
                                    className={styles.title}
                                    variant="body1"
                                    weight="semibold">
                                    Change password
                                </Typography>
                                <Typography className={styles.description} variant="body3">
                                    Password was changed{' '}
                                    {prettifyDate(
                                        new Date(user?.password_change_date as Date),
                                        (val) => val
                                    )}
                                </Typography>
                            </div>
                        </div>
                        <Icon name="nav_right" />
                    </div>
                    <div className={styles.method} onClick={() => setActiveTab('mfa')}>
                        <div className={styles.left}>
                            <div className={styles.icon}>
                                <Icon name="shield_key" />
                            </div>
                            <div className={styles.text_wrapper}>
                                <Typography
                                    className={styles.title}
                                    variant="body1"
                                    weight="semibold">
                                    Two-factor Authentication
                                </Typography>
                                <Typography className={styles.description} variant="body3">
                                    Status:{' '}
                                    {user?.two_fa_method === ETwoFAMethod.NONE ? 'Off' : 'On'}
                                </Typography>
                            </div>
                        </div>
                        <Icon name="nav_right" />
                    </div>
                </div>
            )}
            {activeTab === 'password' && (
                <PasswordSection onSuccess={() => setActiveTab(undefined)} />
            )}
            {activeTab === 'mfa' && <MfaSection />}
        </motion.div>
    );
};

export default SettingsSecurityLayout;
